import { extractChannelId } from "@/lib/ws/routing";
import type { ChannelMessageHandler, RealtimeConnectionState } from "@/lib/ws/types";

const DEFAULT_BASE_RECONNECT_MS = 500;
const DEFAULT_MAX_RECONNECT_MS = 30_000;

export type RealtimeClientOptions = {
  protocols?: string | string[];
  maxReconnectDelayMs?: number;
  baseReconnectDelayMs?: number;
};

/**
 * Channel-oriented WebSocket client: auto-reconnect, exponential backoff, pub/sub per channel.
 * Messages should be JSON with `channel` | `type` | `topic` for routing (see `extractChannelId`).
 */
export class RealtimeClient {
  private readonly url: string;
  private readonly protocols?: string | string[];
  private readonly maxReconnectDelayMs: number;
  private readonly baseReconnectDelayMs: number;

  private ws: WebSocket | null = null;
  private intentionalClose = false;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private reconnectAttempt = 0;
  private state: RealtimeConnectionState = "disconnected";

  private readonly subscribers = new Map<string, Set<ChannelMessageHandler>>();
  private readonly connectionListeners = new Set<(s: RealtimeConnectionState) => void>();

  constructor(url: string, options: RealtimeClientOptions = {}) {
    this.url = url;
    this.protocols = options.protocols;
    this.maxReconnectDelayMs = options.maxReconnectDelayMs ?? DEFAULT_MAX_RECONNECT_MS;
    this.baseReconnectDelayMs = options.baseReconnectDelayMs ?? DEFAULT_BASE_RECONNECT_MS;
  }

  getConnectionState(): RealtimeConnectionState {
    return this.state;
  }

  onConnectionState(listener: (state: RealtimeConnectionState) => void): () => void {
    this.connectionListeners.add(listener);
    listener(this.state);
    return () => {
      this.connectionListeners.delete(listener);
    };
  }

  connect(): void {
    this.intentionalClose = false;
    if (this.ws?.readyState === WebSocket.OPEN || this.ws?.readyState === WebSocket.CONNECTING) {
      return;
    }
    this.openSocket();
  }

  disconnect(): void {
    this.intentionalClose = true;
    if (this.reconnectTimer !== null) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.ws) {
      this.ws.close(1000, "client disconnect");
      this.ws = null;
    }
    this.reconnectAttempt = 0;
    this.setState("disconnected");
  }

  subscribe(channel: string, handler: ChannelMessageHandler): () => void {
    let set = this.subscribers.get(channel);
    if (!set) {
      set = new Set();
      this.subscribers.set(channel, set);
    }
    set.add(handler);
    return () => this.unsubscribe(channel, handler);
  }

  unsubscribe(channel: string, handler?: ChannelMessageHandler): void {
    if (handler === undefined) {
      this.subscribers.delete(channel);
      return;
    }
    const set = this.subscribers.get(channel);
    if (!set) {
      return;
    }
    set.delete(handler);
    if (set.size === 0) {
      this.subscribers.delete(channel);
    }
  }

  private setState(next: RealtimeConnectionState): void {
    if (this.state === next) {
      return;
    }
    this.state = next;
    this.connectionListeners.forEach((fn) => {
      try {
        fn(next);
      } catch {
        /* isolate subscriber errors */
      }
    });
  }

  private scheduleReconnect(): void {
    if (this.intentionalClose || this.reconnectTimer !== null) {
      return;
    }
    const delay = Math.min(
      this.baseReconnectDelayMs * 2 ** this.reconnectAttempt,
      this.maxReconnectDelayMs,
    );
    this.reconnectAttempt += 1;
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.openSocket();
    }, delay);
  }

  private openSocket(): void {
    if (typeof WebSocket === "undefined") {
      return;
    }

    this.setState(this.reconnectAttempt > 0 ? "reconnecting" : "connecting");

    let socket: WebSocket;
    try {
      socket = this.protocols ? new WebSocket(this.url, this.protocols) : new WebSocket(this.url);
    } catch {
      this.setState("reconnecting");
      this.scheduleReconnect();
      return;
    }

    this.ws = socket;

    socket.onopen = () => {
      this.reconnectAttempt = 0;
      this.setState("connected");
    };

    socket.onmessage = (ev) => {
      this.dispatchMessage(ev);
    };

    socket.onerror = () => {
      /* close event drives reconnect */
    };

    socket.onclose = () => {
      this.ws = null;
      if (this.intentionalClose) {
        this.setState("disconnected");
        return;
      }
      this.setState("reconnecting");
      this.scheduleReconnect();
    };
  }

  private dispatchMessage(ev: MessageEvent): void {
    let parsed: unknown = ev.data;
    if (typeof ev.data === "string") {
      try {
        parsed = JSON.parse(ev.data);
      } catch {
        /* non-JSON: still fan-out to '*' */
      }
    }

    const channelId = extractChannelId(parsed);
    if (channelId) {
      const set = this.subscribers.get(channelId);
      if (set) {
        set.forEach((fn) => {
          try {
            fn(parsed, ev);
          } catch {
            /* isolate */
          }
        });
      }
    }

    const star = this.subscribers.get("*");
    if (star) {
      star.forEach((fn) => {
        try {
          fn(parsed, ev);
        } catch {
          /* isolate */
        }
      });
    }
  }
}

export function createRealtimeClient(url: string, options?: RealtimeClientOptions): RealtimeClient {
  return new RealtimeClient(url, options);
}
